import React, { useState, useCallback, useMemo } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";
import DashboardReport from "components/reports/templates/DashboardReport";
import ReportPieChart from "components/reports/charts/ReportPieChart";
import ReportBarChart from "components/reports/charts/ReportBarChart";
import ReportLineChart from "components/reports/charts/ReportLineChart";
import DrillDownDrawer from "components/reports/shared/DrillDownDrawer";
import { getChartColors } from "theme";
import api from "services/api";

const getLatestContract = (client) => {
  const history = Array.isArray(client?.ClientContractHistories)
    ? [...client.ClientContractHistories]
    : [];

  return history.sort((first, second) => {
    const firstTime = new Date(
      first?.Date || first?.RenewDate || first?.RenewalDueDate || 0,
    ).getTime();
    const secondTime = new Date(
      second?.Date || second?.RenewDate || second?.RenewalDueDate || 0,
    ).getTime();
    return secondTime - firstTime;
  })[0];
};

/**
 * Client Overview Report - Dashboard view with key client metrics
 */
export const ClientOverview = ({ filters }) => {
  const [drillDown, setDrillDown] = useState({
    open: false,
    title: "",
    subtitle: "",
    description: "",
    data: [],
    columns: [],
    summary: null,
  });

  const closeDrillDown = useCallback(() => {
    setDrillDown({
      open: false,
      title: "",
      subtitle: "",
      description: "",
      data: [],
      columns: [],
      summary: null,
    });
  }, []);

  const handleContractStatusClick = useCallback((data) => {
    const contractStatuses = data.contractStatusBreakdown || [];

    setDrillDown({
      open: true,
      title: "Client Contract Status",
      subtitle: `${data.totalClientsCount || 0} total clients • ${contractStatuses.length} status groups`,
      description:
        "Shows the latest known contract status for each client based on their most recent contract history entry. Use this view to quickly identify which accounts are current, pending renewal, or missing contracts.",
      data: contractStatuses,
      columns: [
        { field: "status", headerName: "Contract Status", width: 220 },
        { field: "count", headerName: "Clients", width: 110 },
        {
          field: "percentage",
          headerName: "% of Clients",
          width: 130,
          renderCell: (row) => `${row.percentage?.toFixed(1)}%`,
        },
      ],
      expandable: true,
      expandableConfig: {
        getExpandedContent: (statusRow) => ({
          data: statusRow.clients || [],
          columns: [
            { field: "name", headerName: "Client", width: 280 },
            { field: "status", headerName: "Status", width: 180 },
            {
              field: "renewDate",
              headerName: "Renew Date",
              width: 140,
              renderCell: (row) =>
                row.renewDate
                  ? new Date(row.renewDate).toLocaleDateString(undefined, {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })
                  : "-",
            },
          ],
        }),
      },
      summary: {
        metrics: [
          {
            label: "Total Clients",
            value: data.totalClientsCount || 0,
          },
          {
            label: "Active in Period",
            value: data.activeClientsInPeriod || 0,
          },
          {
            label: "Top Status",
            value: data.topContractStatus?.status || "N/A",
          },
        ],
      },
    });
  }, []);

  const handleTopClientsByCategoryClick = useCallback((data) => {
    const topByCategory = data.topClientsByCategory || [];

    setDrillDown({
      open: true,
      title: "Biggest Clients by Service Category",
      subtitle: `${topByCategory.length} categories with active revenue`,
      description:
        "For each service category, this view highlights the highest-revenue client during the selected period. Use it to identify strategic category anchors and concentration by service line.",
      data: topByCategory,
      columns: [
        { field: "category", headerName: "Category", width: 160 },
        { field: "clientName", headerName: "Top Client", width: 260 },
        {
          field: "revenue",
          headerName: "Revenue",
          width: 150,
          renderCell: (row) =>
            `$${(row.revenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        },
        { field: "servicesCount", headerName: "Services", width: 110 },
      ],
      summary: {
        metrics: [
          {
            label: "Categories",
            value: topByCategory.length,
          },
          {
            label: "Highest Category Anchor",
            value: topByCategory[0]?.clientName || "N/A",
          },
        ],
      },
    });
  }, []);

  const handleServiceTypesClick = useCallback((data) => {
    const serviceTypes = data.topServiceTypes || [];

    setDrillDown({
      open: true,
      title: "Top Service Types",
      subtitle: `${data.serviceTypesCount || 0} service types in period`,
      description:
        "Breakdown of service types by revenue and profitability within the selected period. Use this to spot high-value service mixes and prioritize sales focus.",
      data: serviceTypes,
      columns: [
        { field: "serviceType", headerName: "Service Type", width: 230 },
        { field: "category", headerName: "Category", width: 140 },
        {
          field: "revenue",
          headerName: "Revenue",
          width: 140,
          renderCell: (row) =>
            `$${(row.revenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        },
        { field: "servicesCount", headerName: "Services", width: 100 },
        {
          field: "profitMargin",
          headerName: "Margin",
          width: 110,
          renderCell: (row) => `${(row.profitMargin || 0).toFixed(2)}%`,
        },
      ],
      summary: {
        metrics: [
          {
            label: "Service Types",
            value: data.serviceTypesCount || 0,
          },
          {
            label: "Top Type",
            value: data.dominantServiceType || "N/A",
          },
        ],
      },
    });
  }, []);

  const handleContractStatusSliceClick = useCallback((slice, data) => {
    const selectedStatus = data.contractStatusBreakdown?.find(
      (statusRow) => statusRow.status === slice.label,
    );

    if (!selectedStatus) return;

    setDrillDown({
      open: true,
      title: `Contract Status: ${selectedStatus.status}`,
      subtitle: `${selectedStatus.count} clients (${selectedStatus.percentage?.toFixed(1)}%)`,
      description:
        "Clients grouped under this contract status based on their latest contract history record.",
      data: selectedStatus.clients || [],
      columns: [
        { field: "name", headerName: "Client", width: 280 },
        { field: "status", headerName: "Status", width: 180 },
        {
          field: "renewDate",
          headerName: "Renew Date",
          width: 140,
          renderCell: (row) =>
            row.renewDate
              ? new Date(row.renewDate).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })
              : "-",
        },
      ],
      summary: {
        metrics: [
          { label: "Status", value: selectedStatus.status },
          { label: "Clients", value: selectedStatus.count },
          {
            label: "% of Total",
            value: `${selectedStatus.percentage?.toFixed(1)}%`,
          },
        ],
      },
    });
  }, []);

  const handleCategoryUsageSliceClick = useCallback((slice, data) => {
    const selectedCategory = data.categoryUsageBreakdown?.find(
      (categoryRow) => categoryRow.category === slice.label,
    );

    if (!selectedCategory) return;

    setDrillDown({
      open: true,
      title: `Client Usage: ${selectedCategory.category}`,
      subtitle: `${selectedCategory.clientCount} clients • ${selectedCategory.servicesCount} services`,
      description:
        "Client usage details for this service category during the selected period.",
      data: selectedCategory.clients || [],
      columns: [
        { field: "clientName", headerName: "Client", width: 280 },
        { field: "servicesCount", headerName: "Services", width: 110 },
        {
          field: "revenue",
          headerName: "Revenue",
          width: 150,
          renderCell: (row) =>
            `$${(row.revenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        },
      ],
      summary: {
        metrics: [
          { label: "Category", value: selectedCategory.category },
          { label: "Unique Clients", value: selectedCategory.clientCount },
          { label: "Services", value: selectedCategory.servicesCount },
        ],
      },
    });
  }, []);

  const handleClientRetentionClick = useCallback(
    async (data) => {
      try {
        const response = await api.get(
          "/reports/financial/client-retention-details",
          { params: filters },
        );
        const retentionData = response.data.data;

        setDrillDown({
          open: true,
          title: "Client Retention Analysis",
          subtitle: `${retentionData.summary.retentionRate}% retention • ${retentionData.summary.churned} clients lost`,
          description: (
            <Box
              component="div"
              sx={{
                typography: "body2",
                lineHeight: 1.6,
                color: "text.secondary",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, mb: 1.5, color: "info.main" }}
              >
                Client Retention Breakdown
              </Typography>
              <Typography variant="body2" paragraph>
                This analysis compares clients between the{" "}
                <strong>current period</strong> (filtered dates) and the{" "}
                <strong>previous period</strong> of equal length.
              </Typography>
              <Typography variant="body2" component="div" paragraph>
                • <strong>Retained clients:</strong> Had services in BOTH
                periods (continued doing business)
                <br />• <strong>Churned clients:</strong> Had services in
                previous period but NONE in current period (stopped using your
                services - dates shown are from previous period)
                <br />• <strong>New clients:</strong> Started in the current
                period only (no previous history)
              </Typography>
              <Box
                sx={{
                  mt: 1,
                  p: 1.5,
                  bgcolor: "background.alt",
                  borderRadius: 1,
                }}
              >
                <Typography variant="caption" display="block">
                  <strong>Retention Rate:</strong>{" "}
                  {retentionData.summary.retentionRate}%
                </Typography>
                <Typography variant="caption" display="block">
                  <strong>Churn Rate:</strong> {retentionData.summary.churnRate}
                  %
                </Typography>
                <Typography variant="caption" display="block">
                  <strong>Retained:</strong> {retentionData.summary.retained}{" "}
                  clients
                </Typography>
                <Typography variant="caption" display="block">
                  <strong>Churned:</strong> {retentionData.summary.churned}{" "}
                  clients
                </Typography>
                <Typography variant="caption" display="block">
                  <strong>New:</strong> {retentionData.summary.new} clients
                </Typography>
              </Box>
            </Box>
          ),
          data: [
            ...retentionData.retainedClients.map((c) => ({
              ...c,
              status: "Retained",
            })),
            ...retentionData.churnedClients.map((c) => ({
              ...c,
              status: "Churned",
            })),
            ...retentionData.newClients.map((c) => ({ ...c, status: "New" })),
          ],
          columns: [
            { field: "name", headerName: "Client Name", width: 250 },
            {
              field: "status",
              headerName: "Status",
              width: 120,
              renderCell: (row) => (
                <Typography
                  variant="body2"
                  sx={{
                    color:
                      row.status === "Retained"
                        ? "success.main"
                        : row.status === "Churned"
                          ? "error.main"
                          : "info.main",
                    fontWeight: 600,
                  }}
                >
                  {row.status}
                </Typography>
              ),
            },
            {
              field: "revenue",
              headerName: "Current Revenue",
              width: 150,
              renderCell: (row) =>
                row.revenue
                  ? `$${row.revenue?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                  : "-",
            },
            {
              field: "previousRevenue",
              headerName: "Previous Revenue",
              width: 150,
              renderCell: (row) =>
                row.previousRevenue
                  ? `$${row.previousRevenue?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                  : "-",
            },
            {
              field: "revenueChange",
              headerName: "Change",
              width: 120,
              renderCell: (row) => {
                if (row.revenueChange == null) return "-";
                const changeSymbol = row.revenueChange > 0 ? "+" : "";
                return `${changeSymbol}$${row.revenueChange?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
              },
            },
            { field: "servicesCount", headerName: "Services", width: 100 },
            {
              field: "lastServiceDate",
              headerName: "Last Service Date",
              width: 140,
              renderCell: (row) =>
                row.lastServiceDate
                  ? new Date(row.lastServiceDate).toLocaleDateString(
                      undefined,
                      { year: "numeric", month: "short", day: "numeric" },
                    )
                  : "-",
            },
          ],
          summary: {
            metrics: [
              {
                label: "Retention Rate",
                value: `${retentionData.summary.retentionRate}%`,
                color:
                  retentionData.summary.retentionRate >= 90
                    ? "success.main"
                    : "warning.main",
              },
              {
                label: "Churn Rate",
                value: `${retentionData.summary.churnRate}%`,
                color:
                  retentionData.summary.churnRate <= 10
                    ? "success.main"
                    : "error.main",
              },
              {
                label: "Retained Clients",
                value: retentionData.summary.retained,
                color: "success.main",
              },
              {
                label: "Churned Clients",
                value: retentionData.summary.churned,
                color: "error.main",
              },
              {
                label: "New Clients",
                value: retentionData.summary.new,
                color: "info.main",
              },
            ],
          },
        });
      } catch (error) {
        console.error("Error fetching client retention details:", error);
      }
    },
    [filters],
  );

  const handleInvoicePaymentClick = useCallback(
    async (data) => {
      try {
        const response = await api.get("/reports/financial/payment-ranges", {
          params: filters,
        });
        const paymentData = response.data.data;

        setDrillDown({
          open: true,
          title: "Invoice Payment Analysis by Range",
          subtitle: `${paymentData.summary.totalClients} clients • ${paymentData.summary.totalInvoices} invoices`,
          description: (
            <Box
              component="div"
              sx={{
                typography: "body2",
                lineHeight: 1.6,
                color: "text.secondary",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, mb: 1.5, color: "info.main" }}
              >
                Payment Speed Breakdown
              </Typography>
              <Typography variant="body2" paragraph>
                Clients grouped by their average payment speed (days from
                invoice due date to payment received). Click on any payment
                range to see which clients fall into that category. Faster
                payment = better cash flow.
              </Typography>
              <Typography
                variant="body2"
                paragraph
                sx={{ fontWeight: 500, color: "warning.main" }}
              >
                💡 Focus on slow payers (60+ days) - they may need payment terms
                review or automated reminders.
              </Typography>
            </Box>
          ),
          data: paymentData.ranges || [],
          columns: [
            { field: "label", headerName: "Payment Range", width: 150 },
            { field: "clientCount", headerName: "Clients", width: 100 },
            {
              field: "totalRevenue",
              headerName: "Total Revenue",
              width: 150,
              renderCell: (row) =>
                `$${row.totalRevenue?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            },
          ],
          expandable: true,
          expandableConfig: {
            getExpandedContent: (range) => ({
              data: range.clients || [],
              columns: [
                { field: "clientName", headerName: "Client Name", width: 300 },
                {
                  field: "avgPaymentDays",
                  headerName: "Avg Days to Payment",
                  width: 180,
                  renderCell: (row) => (
                    <Typography
                      variant="body2"
                      sx={{
                        color:
                          row.avgPaymentDays <= 30
                            ? "success.main"
                            : row.avgPaymentDays <= 60
                              ? "warning.main"
                              : "error.main",
                        fontWeight: 600,
                      }}
                    >
                      {row.avgPaymentDays} days
                    </Typography>
                  ),
                },
                { field: "invoiceCount", headerName: "Invoices", width: 100 },
                {
                  field: "totalRevenue",
                  headerName: "Total Revenue",
                  width: 150,
                  renderCell: (row) =>
                    `$${row.totalRevenue?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                },
              ],
            }),
          },
          summary: {
            metrics: [
              {
                label: "Total Clients",
                value: paymentData.summary.totalClients,
              },
              {
                label: "Total Invoices",
                value: paymentData.summary.totalInvoices,
              },
              {
                label: "Payment Ranges",
                value: paymentData.ranges?.length || 0,
              },
            ],
          },
        });
      } catch (error) {
        console.error("Error fetching payment ranges:", error);
      }
    },
    [filters],
  );

  const metricsConfig = useCallback(
    (data) => {
      return [
        {
          groupTitle: "Client Health & Retention",
          metrics: [
            {
              title: "Customer Lifetime Value",
              value: `$${data.customerLifetimeValue?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0.00"}`,
              subtitle: "Avg per client",
              tooltipTitle: "Customer Lifetime Value (CLV)",
              tooltipContent:
                "Customer Lifetime Value estimates the total revenue you'll earn from a client throughout your entire business relationship.\n\nCLV helps you answer:\n• How much can I spend to acquire a new client?\n• Which client segments are most valuable?\n• Is our client relationship strength improving?\n\nRule of Thumb: You can typically afford to spend up to 1/3 of CLV on client acquisition (sales and marketing costs).\n\nExample: If CLV is $30,000, you could spend up to $10,000 to acquire that client and still be profitable.\n\nWhy it matters: Higher CLV means stronger client relationships and better long-term business health. Focus on increasing CLV through excellent service, regular communication, and identifying client needs before they ask. A 10% increase in client retention can increase CLV by 30% or more.",
            },
            {
              title: "Revenue Concentration",
              value: `${data.concentrationRatio?.toFixed(2) || "0.00"}%`,
              subtitle: "From top 20% of clients",
              color:
                data.concentrationRatio >= 80 ? "warning.main" : "success.main",
              tooltipTitle: "Revenue Concentration (Dependency Risk)",
              tooltipContent:
                "Revenue Concentration = (Revenue from top 20% of clients ÷ Total Revenue) × 100\n\nThis measures how much of your revenue comes from your largest clients. It's a key business risk indicator.\n\nThe Pareto Principle (80/20 rule) suggests that typically 80% of results come from 20% of efforts. In business, this often means a small number of clients drive most revenue.\n\nRisk Assessment:\n🟢 Low Risk (50-70%): Healthy diversification - losing one client won't devastate you\n🟡 Moderate Risk (70-80%): Some concentration - monitor top clients closely\n🔴 High Risk (>80%): Dangerous dependency - losing 1-2 clients could be catastrophic\n\nReal-world example:\nIf you have 100 clients and your top 20 clients represent 85% of revenue:\n• You're heavily dependent on those 20 relationships\n• Losing just ONE top client could mean losing 4-5% of total revenue\n• Your business stability is tied to a small group\n\nBalancing act:\n• Too concentrated (>80%): High risk, but often more profitable (easier to manage fewer relationships)\n• Too diversified (<50%): Lower risk, but may indicate you're serving too many small, unprofitable accounts\n\nAction items if concentration is high:\n1. Invest heavily in retaining top clients (they're your lifeline)\n2. Actively work to grow mid-tier clients to reduce dependency\n3. Ensure you have strong contracts and SLAs with major accounts\n4. Diversify your client base through targeted sales efforts",
            },
            {
              title: "Client Retention Rate",
              value: `${data.clientRetentionRate?.toFixed(2)}%`,
              subtitle: `${data.clientChurnRate?.toFixed(2)}% churn`,
              color:
                data.clientRetentionRate >= 90
                  ? "success.main"
                  : "warning.main",
              tooltipTitle: "Client Retention Rate & Churn",
              tooltipContent:
                "Client Retention Rate = (Clients who stayed ÷ Total clients from last period) × 100\n\nThis measures what percentage of your client base continues doing business with you period over period. The inverse metric is Churn Rate - the percentage who stopped using your services.\n\nExample: If you had 100 clients last year and 90 of them are still clients this year, your retention rate is 90% and churn rate is 10%.\n\nIndustry Benchmarks:\n🟢 Excellent: 90-95% retention (5-10% churn)\n🟡 Acceptable: 80-89% retention (11-20% churn)\n🔴 Concerning: Below 80% retention (>20% churn)\n\nWhy it matters:\n• Retaining clients is 5-25x cheaper than acquiring new ones\n• High churn indicates service quality, pricing, or relationship problems\n• Even small improvements in retention dramatically increase lifetime value\n• Example: Improving from 85% to 90% retention means you keep 5 more clients per 100, which compounds yearly\n\nIf retention is low, investigate: Service quality issues? Pricing concerns? Better competitor offers? Poor communication? Proactive outreach can prevent churn.",
              onClick: () => handleClientRetentionClick(data),
            },
            {
              title: "Invoice-to-Payment",
              value: `${data.avgInvoiceToPaymentDays} days`,
              subtitle: "Average collection time",
              color:
                data.avgInvoiceToPaymentDays <= 30
                  ? "success.main"
                  : data.avgInvoiceToPaymentDays <= 45
                    ? "warning.main"
                    : "error.main",
              tooltipTitle: "Invoice-to-Payment Cycle (Days Sales Outstanding)",
              tooltipContent:
                "Average number of days between sending an invoice and receiving payment. Also called Days Sales Outstanding (DSO).\n\nFormula: Sum of (Payment Date - Invoice Date) ÷ Number of Invoices\n\nThis is a critical cash flow metric. You've delivered the service and earned the revenue, but until the client pays, you don't have the cash to cover your own expenses (like paying linguists!).\n\nBenchmarks:\n🟢 Excellent: 0-30 days (payment within a month)\n🟡 Acceptable: 31-45 days (industry standard for B2B)\n🔴 Concerning: 46-60 days (cash flow strain)\n⚫ Critical: 60+ days (serious cash flow problems, collection issues)\n\nWhy it matters - The Cash Flow Gap:\nEven if you're profitable on paper, slow collections can create cash crunches. You may need to pay linguists and operating expenses before clients pay you.\n\nExample:\n• You deliver a $10,000 service, pay linguist $6,000\n• If client pays in 15 days: Great! You have cash to operate\n• If client pays in 90 days: You're out $6,000 for 3 months - that's YOUR money financing THEIR business\n\nImproving collection time:\n1. Offer early payment discounts (2% off if paid within 10 days)\n2. Send invoices immediately after service delivery\n3. Set up automatic payment reminders (at 15, 30, 45 days)\n4. Require deposits or milestone payments for large projects\n5. Consider credit card or ACH auto-pay for regular clients\n6. Review payment terms in contracts (Net 30 vs Net 60)\n7. Follow up personally on overdue invoices\n\nIf your average is high, segment by client - are a few slow payers dragging down the average? Address them specifically.",
              onClick: () => handleInvoicePaymentClick(data),
            },
          ],
        },
        {
          groupTitle: "Client Portfolio & Mix",
          metrics: [
            {
              title: "Total Clients",
              value: data.totalClientsCount || 0,
              subtitle: "All client accounts",
              tooltipTitle: "Total Client Base",
              tooltipContent:
                "The total number of client accounts in your system, regardless of activity in the selected period. This provides portfolio size context compared to active client counts.",
            },
            {
              title: "Active Clients",
              value: data.activeClientsInPeriod || 0,
              subtitle: `${data.totalClientsCount > 0 ? (((data.activeClientsInPeriod || 0) / data.totalClientsCount) * 100).toFixed(1) : "0.0"}% active in selected period`,
              color:
                data.totalClientsCount > 0 &&
                (data.activeClientsInPeriod || 0) / data.totalClientsCount >= 0.7
                  ? "success.main"
                  : "warning.main",
              tooltipTitle: "Active Clients in Filtered Period",
              tooltipContent:
                "Number of unique clients that generated at least one service during the selected date range. Compare this to total clients to monitor engagement and reactivation opportunity.",
            },
            {
              title: "Contract Status",
              value: data.topContractStatus?.status || "N/A",
              subtitle: `${data.topContractStatus?.count || 0} clients (${data.topContractStatus?.percentage?.toFixed(1) || "0.0"}%)`,
              tooltipTitle: "Latest Contract Status Distribution",
              tooltipContent:
                "Uses each client's latest contract history record to summarize the current contract state of your portfolio (e.g., Current, Sent, Filed, Needs Contract). Click to view status-level client lists.",
              onClick: () => handleContractStatusClick(data),
            },
            {
              title: "Biggest Clients by Category",
              value: data.topClientsByCategory?.length || 0,
              subtitle: data.topClientsByCategory?.[0]
                ? `${data.topClientsByCategory[0].category}: ${data.topClientsByCategory[0].clientName}`
                : "No category data",
              tooltipTitle: "Top Client in Each Service Category",
              tooltipContent:
                "Identifies the highest-revenue client for each service category in the selected period. Useful for spotting category-level dependency and growth targets.",
              onClick: () => handleTopClientsByCategoryClick(data),
            },
            {
              title: "Service Types",
              value: data.serviceTypesCount || 0,
              subtitle: data.dominantServiceType
                ? `Top: ${data.dominantServiceType}`
                : "No service type data",
              tooltipTitle: "Service Type Mix",
              tooltipContent:
                "Count of distinct service types delivered in the period, plus the dominant type by revenue. Click to inspect top service types and margins.",
              onClick: () => handleServiceTypesClick(data),
            },
          ],
        },
      ];
    },
    [
      handleClientRetentionClick,
      handleContractStatusClick,
      handleInvoicePaymentClick,
      handleServiceTypesClick,
      handleTopClientsByCategoryClick,
    ],
  );

  const chartsConfig = useCallback((data) => {
    const handleConcentrationClick = (item) => {
      if (item.id === 1) {
        // Top 20% clients
        const top20Count = Math.ceil((data.topClients?.length || 0) * 0.2);
        const top20Clients = data.topClients?.slice(0, top20Count) || [];

        setDrillDown({
          open: true,
          title: "Top 20% of Clients (by Revenue)",
          subtitle: `${top20Clients.length} clients generating $${data.top20ClientsRevenue?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          description:
            "These are your most valuable client relationships - they represent the top 20% of your client base by count but drive a disproportionate amount of revenue. Guard these relationships carefully and ensure they receive exceptional service. Losing even one could significantly impact your business. Consider: Do you have dedicated account managers for these clients? Are you proactively identifying their needs? How can you deepen these relationships?",
          data: top20Clients,
          columns: [
            { field: "fullName", headerName: "Client Name", width: 300 },
            {
              field: "revenue",
              headerName: "Revenue",
              width: 150,
              renderCell: (row) =>
                `$${row.revenue?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            },
            { field: "servicesCount", headerName: "Services", width: 100 },
            {
              field: "percentOfTotal",
              headerName: "% of Total",
              width: 120,
              renderCell: (row) => `${row.percentOfTotal?.toFixed(2)}%`,
            },
          ],
          summary: {
            metrics: [
              { label: "Client Count", value: top20Clients.length },
              {
                label: "Total Revenue",
                value: `$${data.top20ClientsRevenue?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
              },
              {
                label: "% of Total",
                value: `${((data.top20ClientsRevenue / data.totalRevenue) * 100).toFixed(1)}%`,
              },
            ],
          },
        });
      } else {
        // Other 80% clients
        const top20Count = Math.ceil((data.topClients?.length || 0) * 0.2);
        const other80Clients = data.topClients?.slice(top20Count) || [];
        const other80Revenue =
          (data.totalRevenue || 0) - (data.top20ClientsRevenue || 0);

        setDrillDown({
          open: true,
          title: "Other 80% of Clients (by Revenue)",
          subtitle: `${other80Clients.length} clients generating $${other80Revenue?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          description:
            "These clients represent the bulk of your client base by count but contribute a smaller portion of revenue. Some may be new relationships with growth potential, others may be small accounts that require disproportionate effort. Analyze this list to identify: Which clients could be grown into top-tier accounts? Which are profitable but will remain small? Are any costing more to serve than they generate?",
          data: other80Clients,
          columns: [
            { field: "fullName", headerName: "Client Name", width: 300 },
            {
              field: "revenue",
              headerName: "Revenue",
              width: 150,
              renderCell: (row) =>
                `$${row.revenue?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            },
            { field: "servicesCount", headerName: "Services", width: 100 },
            {
              field: "percentOfTotal",
              headerName: "% of Total",
              width: 120,
              renderCell: (row) => `${row.percentOfTotal?.toFixed(2)}%`,
            },
          ],
          summary: {
            metrics: [
              { label: "Client Count", value: other80Clients.length },
              {
                label: "Total Revenue",
                value: `$${other80Revenue?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
              },
              {
                label: "% of Total",
                value: `${((other80Revenue / data.totalRevenue) * 100).toFixed(1)}%`,
              },
            ],
          },
        });
      }
    };

    return (
      <Grid container spacing={3}>
        {/* Revenue Concentration Analysis */}
        <Grid size={{ xs: 12, md: 6 }}>
          <ReportPieChart
            title="Revenue Concentration Analysis"
            tooltipTitle="80/20 Rule Analysis (Pareto Principle)"
            tooltipContent={`This chart applies the famous Pareto Principle to your client base, showing what percentage of revenue comes from your top 20% of clients versus the other 80%.

The Pareto Principle (discovered by economist Vilfredo Pareto):
'80% of outcomes come from 20% of causes'

In business, this typically means:
• 80% of revenue comes from 20% of clients
• 80% of complaints come from 20% of clients
• 80% of profits come from 20% of products

What this chart reveals:
If the TOP 20% slice is very large (>80% of revenue):
🔴 HIGH RISK: You're highly dependent on a few key relationships. Losing even one could be devastating.
+ Action: Invest heavily in retaining these clients + diversify client base

If the TOP 20% slice is moderate (60-75% of revenue):
🟡 BALANCED: Healthy concentration with some diversification
+ Action: Standard account management practices

If the TOP 20% slice is small (<60% of revenue):
🟢 DIVERSIFIED: Revenue well distributed across client base
+ Action: Identify if you can grow any clients into larger accounts

Why concentration happens:
• Natural market dynamics (some clients simply need more services)
• Better relationships with certain clients
• Industry-specific factors (healthcare vs. legal clients may have different volume needs)

Strategic implications:
• High concentration = Higher profit margins (easier to serve fewer clients well) BUT higher risk
• Low concentration = Lower risk BUT potentially lower margins (more overhead managing many small accounts)

Click either slice to see the detailed client list with revenue breakdown and strategic recommendations.`}
            onSliceClick={(item) => {
              handleConcentrationClick(item);
            }}
            data={[
              {
                id: 1,
                label: "Top 20% Clients",
                value: data.top20ClientsRevenue || 0,
              },
              {
                id: 2,
                label: "Other 80% Clients",
                value:
                  (data.totalRevenue || 0) - (data.top20ClientsRevenue || 0),
              },
            ]}
            height={350}
            showLegend={true}
            valueFormatter={(value) =>
              value != null
                ? `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                : "$0.00"
            }
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <ReportPieChart
            title="Contract Status Distribution"
            tooltipTitle="Contract Status by Client"
            tooltipContent="Shows how your client base is distributed by latest contract status. Click a slice to open the client list for that status."
            onSliceClick={(item) => handleContractStatusSliceClick(item, data)}
            data={(data.contractStatusBreakdown || []).map((item, idx) => ({
              id: idx + 1,
              label: item.status,
              value: item.count,
            }))}
            height={350}
            showLegend={true}
            valueFormatter={(value) => `${value || 0} clients`}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <ReportPieChart
            title="Client Usage by Service Category"
            tooltipTitle="Unique Clients by Category"
            tooltipContent="Displays unique client count using each service category in the selected period. Click a slice for client-level usage details."
            onSliceClick={(item) => handleCategoryUsageSliceClick(item, data)}
            data={(data.categoryUsageBreakdown || []).map((item, idx) => ({
              id: idx + 1,
              label: item.category,
              value: item.clientCount,
            }))}
            height={350}
            showLegend={true}
            valueFormatter={(value) => `${value || 0} clients`}
          />
        </Grid>

      </Grid>
    );
  }, [
    handleCategoryUsageSliceClick,
    handleContractStatusSliceClick,
  ]);

  const dataFetchFn = useCallback(async (activeFilters) => {
    const [summaryResponse, clientsResponse, serviceTypesResponse, categoryServicesResponse] =
      await Promise.all([
        api.get("/reports/financial/revenue-summary", {
          params: activeFilters,
        }),
        api.get("/clients"),
        api.get("/reports/financial/revenue-by-service", {
          params: activeFilters,
        }),
        api.get("/reports/financial/services-by-category", {
          params: activeFilters,
        }),
      ]);

    const summaryData = summaryResponse.data?.data || {};
    const allClients = Array.isArray(clientsResponse.data)
      ? clientsResponse.data
      : [];
    const serviceTypeTableData =
      serviceTypesResponse.data?.data?.tableData || [];
    const servicesByCategoryRows =
      categoryServicesResponse.data?.data?.services || [];

    const contractStatusMap = {};
    allClients.forEach((client) => {
      const clientContracts = Array.isArray(client.ClientContractHistories)
        ? [...client.ClientContractHistories]
        : [];

      const latestContract = clientContracts.sort((a, b) => {
        const firstDate = new Date(a.Date || a.RenewDate || 0).getTime();
        const secondDate = new Date(b.Date || b.RenewDate || 0).getTime();
        return secondDate - firstDate;
      })[0];

      const status = latestContract?.Status || "Needs Contract";
      if (!contractStatusMap[status]) {
        contractStatusMap[status] = {
          status,
          count: 0,
          clients: [],
        };
      }

      contractStatusMap[status].count += 1;
      contractStatusMap[status].clients.push({
        id: client.id,
        name: client.Account || client.ShortName || `Client ${client.id}`,
        status,
        renewDate: latestContract?.RenewDate || latestContract?.RenewalDueDate,
      });
    });

    const contractStatusBreakdown = Object.values(contractStatusMap)
      .map((statusRow) => ({
        ...statusRow,
        percentage:
          allClients.length > 0 ? (statusRow.count / allClients.length) * 100 : 0,
      }))
      .sort((a, b) => b.count - a.count);

    const categoryUsageMap = {};
    servicesByCategoryRows.forEach((service) => {
      const category = service.category || "Uncategorized";
      const clientName = service.clientName || "Unknown Client";

      if (!categoryUsageMap[category]) {
        categoryUsageMap[category] = {
          category,
          servicesCount: 0,
          totalRevenue: 0,
          clientsMap: {},
        };
      }

      categoryUsageMap[category].servicesCount += 1;
      categoryUsageMap[category].totalRevenue += Number(service.AccountCharge || 0);

      if (!categoryUsageMap[category].clientsMap[clientName]) {
        categoryUsageMap[category].clientsMap[clientName] = {
          clientName,
          servicesCount: 0,
          revenue: 0,
        };
      }

      categoryUsageMap[category].clientsMap[clientName].servicesCount += 1;
      categoryUsageMap[category].clientsMap[clientName].revenue += Number(
        service.AccountCharge || 0,
      );
    });

    const categoryUsageBreakdown = Object.values(categoryUsageMap)
      .map((categoryRow) => ({
        category: categoryRow.category,
        servicesCount: categoryRow.servicesCount,
        totalRevenue: categoryRow.totalRevenue,
        clientCount: Object.keys(categoryRow.clientsMap).length,
        clients: Object.values(categoryRow.clientsMap).sort(
          (a, b) => b.revenue - a.revenue,
        ),
      }))
      .sort((a, b) => b.clientCount - a.clientCount);

    const categoryClientRevenueMap = {};
    servicesByCategoryRows.forEach((service) => {
      const category = service.category || "Uncategorized";
      const clientName = service.clientName || "Unknown Client";
      const key = `${category}|||${clientName}`;

      if (!categoryClientRevenueMap[key]) {
        categoryClientRevenueMap[key] = {
          category,
          clientName,
          revenue: 0,
          servicesCount: 0,
        };
      }

      categoryClientRevenueMap[key].revenue += Number(service.AccountCharge || 0);
      categoryClientRevenueMap[key].servicesCount += 1;
    });

    const topByCategoryMap = {};
    Object.values(categoryClientRevenueMap).forEach((clientCategoryRow) => {
      const existing = topByCategoryMap[clientCategoryRow.category];
      if (!existing || clientCategoryRow.revenue > existing.revenue) {
        topByCategoryMap[clientCategoryRow.category] = clientCategoryRow;
      }
    });

    const topClientsByCategory = Object.values(topByCategoryMap).sort(
      (a, b) => b.revenue - a.revenue,
    );

    const activeClientsInPeriod = Array.isArray(summaryData.topClients)
      ? summaryData.topClients.length
      : 0;

    const dominantServiceType = serviceTypeTableData[0]?.serviceType || "N/A";

    return {
      success: true,
      data: {
        ...summaryData,
        totalClientsCount: allClients.length,
        activeClientsInPeriod,
        contractStatusBreakdown,
        topContractStatus: contractStatusBreakdown[0] || null,
        topClientsByCategory,
        categoryUsageBreakdown,
        serviceTypesCount: serviceTypeTableData.length,
        topServiceTypes: serviceTypeTableData.slice(0, 10),
        dominantServiceType,
      },
    };
  }, []);

  return (
    <>
      <DashboardReport
        title="Client Overview"
        description="Key metrics about your client relationships, retention, and payment behavior"
        dataFetchFn={dataFetchFn}
        filters={filters}
        metricsConfig={metricsConfig}
        chartsConfig={chartsConfig}
      />
      <DrillDownDrawer
        open={drillDown.open}
        onClose={closeDrillDown}
        title={drillDown.title}
        subtitle={drillDown.subtitle}
        description={drillDown.description}
        data={drillDown.data}
        columns={drillDown.columns}
        summary={drillDown.summary}
      />
    </>
  );
};

/**
 * Client Directory & Contracts - Directory and contract health for all clients
 */
export const ClientDirectoryContracts = ({ filters }) => {
  const [drillDown, setDrillDown] = useState({
    open: false,
    title: "",
    subtitle: "",
    description: "",
    data: [],
    columns: [],
    summary: null,
  });

  const closeDrillDown = useCallback(() => {
    setDrillDown({
      open: false,
      title: "",
      subtitle: "",
      description: "",
      data: [],
      columns: [],
      summary: null,
    });
  }, []);

  const baseColumns = useMemo(
    () => [
      { field: "name", headerName: "Client", width: 260 },
      { field: "shortName", headerName: "Short Name", width: 160 },
      { field: "type", headerName: "Type", width: 140 },
      { field: "status", headerName: "Contract Status", width: 170 },
      {
        field: "renewDate",
        headerName: "Renew Date",
        width: 140,
        renderCell: (row) =>
          row.renewDate
            ? new Date(row.renewDate).toLocaleDateString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
              })
            : "-",
      },
      {
        field: "renewalDueDate",
        headerName: "Renewal Due",
        width: 140,
        renderCell: (row) =>
          row.renewalDueDate
            ? new Date(row.renewalDueDate).toLocaleDateString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
              })
            : "-",
      },
    ],
    [],
  );

  const openDirectory = useCallback((data) => {
    setDrillDown({
      open: true,
      title: "Client Directory",
      subtitle: `${data.totalClients} total clients`,
      description:
        "Full client directory with latest contract details and renewal timelines.",
      data: data.directoryRows || [],
      columns: baseColumns,
      summary: {
        metrics: [
          { label: "Total Clients", value: data.totalClients || 0 },
          {
            label: "With Contract",
            value: data.clientsWithContract || 0,
          },
          {
            label: "Needs Contract",
            value: data.clientsNeedingContract || 0,
          },
        ],
      },
    });
  }, [baseColumns]);

  const openNeedsContract = useCallback((data) => {
    setDrillDown({
      open: true,
      title: "Clients Needing Contract",
      subtitle: `${data.clientsNeedingContract || 0} clients`,
      description:
        "Clients whose latest contract status is missing or indicates a contract is needed.",
      data: data.needsContractRows || [],
      columns: baseColumns,
    });
  }, [baseColumns]);

  const openRenewalDueSoon = useCallback((data) => {
    setDrillDown({
      open: true,
      title: "Contracts Due in 60 Days",
      subtitle: `${data.renewalDueSoon || 0} clients`,
      description:
        "Clients with contract renewals due in the next 60 days based on latest contract records.",
      data: data.renewalDueSoonRows || [],
      columns: baseColumns,
    });
  }, [baseColumns]);

  const openStatusSlice = useCallback((slice, data) => {
    const selectedStatus = data.contractStatusBreakdown?.find(
      (statusRow) => statusRow.status === slice.label,
    );
    if (!selectedStatus) return;

    setDrillDown({
      open: true,
      title: `Contract Status: ${selectedStatus.status}`,
      subtitle: `${selectedStatus.count} clients (${selectedStatus.percentage?.toFixed(1)}%)`,
      description: "Client list filtered by selected contract status.",
      data: selectedStatus.clients || [],
      columns: baseColumns,
    });
  }, [baseColumns]);

  const metricsConfig = useCallback(
    (data) => [
      {
        groupTitle: "Directory & Contracts",
        metrics: [
          {
            title: "Total Clients",
            value: data.totalClients || 0,
            subtitle: "All directory records",
            onClick: () => openDirectory(data),
          },
          {
            title: "Clients With Contract",
            value: data.clientsWithContract || 0,
            subtitle: `${data.totalClients > 0 ? (((data.clientsWithContract || 0) / data.totalClients) * 100).toFixed(1) : "0.0"}% of directory`,
          },
          {
            title: "Needs Contract",
            value: data.clientsNeedingContract || 0,
            subtitle: "Missing or flagged statuses",
            color:
              (data.clientsNeedingContract || 0) > 0
                ? "warning.main"
                : "success.main",
            onClick: () => openNeedsContract(data),
          },
          {
            title: "Renewal Due (60d)",
            value: data.renewalDueSoon || 0,
            subtitle: "Upcoming contract actions",
            color:
              (data.renewalDueSoon || 0) > 0 ? "warning.main" : "success.main",
            onClick: () => openRenewalDueSoon(data),
          },
        ],
      },
    ],
    [openDirectory, openNeedsContract, openRenewalDueSoon],
  );

  const chartsConfig = useCallback(
    (data) => (
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <ReportPieChart
            title="Contract Status Distribution"
            tooltipTitle="Contract Status Mix"
            tooltipContent="Distribution of clients by latest contract status. Click any slice to view matching clients."
            data={(data.contractStatusBreakdown || []).map((item, idx) => ({
              id: idx + 1,
              label: item.status,
              value: item.count,
            }))}
            onSliceClick={(slice) => openStatusSlice(slice, data)}
            height={340}
            showLegend={true}
            valueFormatter={(value) => `${value || 0} clients`}
          />
        </Grid>
      </Grid>
    ),
    [openStatusSlice],
  );

  const dataFetchFn = useCallback(async () => {
    const response = await api.get("/clients");
    const clients = Array.isArray(response.data) ? response.data : [];
    const today = new Date();
    const sixtyDaysLater = new Date();
    sixtyDaysLater.setDate(today.getDate() + 60);

    const directoryRows = clients.map((client) => {
      const latestContract = getLatestContract(client);
      const status = latestContract?.Status || "Needs Contract";

      return {
        id: client.id,
        name: client.Account || client.ShortName || `Client ${client.id}`,
        shortName: client.ShortName || "-",
        type: client.Type || "-",
        status,
        renewDate: latestContract?.RenewDate || null,
        renewalDueDate: latestContract?.RenewalDueDate || null,
      };
    });

    const needsContractRows = directoryRows.filter(
      (row) => !row.status || row.status === "Needs Contract" || row.status === "N/A",
    );

    const renewalDueSoonRows = directoryRows.filter((row) => {
      if (!row.renewDate) return false;
      const renewDate = new Date(row.renewDate);
      return renewDate >= today && renewDate <= sixtyDaysLater;
    });

    const statusMap = {};
    directoryRows.forEach((row) => {
      const status = row.status || "Needs Contract";
      if (!statusMap[status]) {
        statusMap[status] = { status, count: 0, clients: [] };
      }
      statusMap[status].count += 1;
      statusMap[status].clients.push(row);
    });

    const contractStatusBreakdown = Object.values(statusMap)
      .map((statusRow) => ({
        ...statusRow,
        percentage:
          directoryRows.length > 0
            ? (statusRow.count / directoryRows.length) * 100
            : 0,
      }))
      .sort((a, b) => b.count - a.count);

    return {
      success: true,
      data: {
        totalClients: directoryRows.length,
        clientsWithContract: directoryRows.length - needsContractRows.length,
        clientsNeedingContract: needsContractRows.length,
        renewalDueSoon: renewalDueSoonRows.length,
        directoryRows,
        needsContractRows,
        renewalDueSoonRows,
        contractStatusBreakdown,
      },
    };
  }, []);

  return (
    <>
      <DashboardReport
        title="Client Directory & Contracts"
        description="Client directory with latest contract status and renewal visibility"
        dataFetchFn={dataFetchFn}
        filters={filters}
        metricsConfig={metricsConfig}
        chartsConfig={chartsConfig}
      />
      <DrillDownDrawer
        open={drillDown.open}
        onClose={closeDrillDown}
        title={drillDown.title}
        subtitle={drillDown.subtitle}
        description={drillDown.description}
        data={drillDown.data}
        columns={drillDown.columns}
        summary={drillDown.summary}
      />
    </>
  );
};

/**
 * Client Activity & Service Mix - Service volume, recency, and mix analysis
 */
export const ClientActivityServiceMix = ({ filters }) => {
  const [drillDown, setDrillDown] = useState({
    open: false,
    title: "",
    subtitle: "",
    description: "",
    data: [],
    columns: [],
    summary: null,
  });

  const closeDrillDown = useCallback(() => {
    setDrillDown({
      open: false,
      title: "",
      subtitle: "",
      description: "",
      data: [],
      columns: [],
      summary: null,
    });
  }, []);

  const currencyCell = useCallback(
    (value) =>
    `$${(value || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`,
    [],
  );

  const activityColumns = useMemo(
    () => [
      { field: "clientName", headerName: "Client", width: 260 },
      { field: "clientType", headerName: "Type", width: 140 },
      { field: "servicesCount", headerName: "Services", width: 100 },
      {
        field: "revenue",
        headerName: "Revenue",
        width: 140,
        renderCell: (row) => currencyCell(row.revenue),
      },
      {
        field: "avgRevenuePerService",
        headerName: "Avg/Service",
        width: 130,
        renderCell: (row) => currencyCell(row.avgRevenuePerService),
      },
      {
        field: "lastServiceDate",
        headerName: "Last Service",
        width: 130,
        renderCell: (row) =>
          row.lastServiceDate
            ? new Date(row.lastServiceDate).toLocaleDateString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
              })
            : "-",
      },
      {
        field: "daysSinceLastService",
        headerName: "Days Since",
        width: 110,
        renderCell: (row) => row.daysSinceLastService ?? "-",
      },
    ],
    [currencyCell],
  );

  const handleAtRiskClientsClick = useCallback((data) => {
    setDrillDown({
      open: true,
      title: "At-Risk Clients (No Service > 60 Days)",
      subtitle: `${data.atRiskClientsCount || 0} clients`,
      description:
        "Clients with more than 60 days since last service activity in the selected period.",
      data: data.atRiskClients || [],
      columns: activityColumns,
    });
  }, [activityColumns]);

  const handleClientTypeSliceClick = useCallback((slice, data) => {
    const selectedType = data.clientTypeBreakdown?.find(
      (typeRow) => typeRow.type === slice.label,
    );
    if (!selectedType) return;

    setDrillDown({
      open: true,
      title: `Client Type: ${selectedType.type}`,
      subtitle: `${selectedType.count} clients • ${selectedType.servicesCount} services`,
      description:
        "Client activity for the selected client type, including service volume and revenue.",
      data: selectedType.clients || [],
      columns: activityColumns,
      summary: {
        metrics: [
          { label: "Type", value: selectedType.type },
          { label: "Clients", value: selectedType.count },
          { label: "Services", value: selectedType.servicesCount },
          { label: "Revenue", value: currencyCell(selectedType.revenue) },
        ],
      },
    });
  }, [activityColumns, currencyCell]);

  const handleCategorySliceClick = useCallback((slice, data) => {
    const selectedCategory = data.categoryMixBreakdown?.find(
      (row) => row.category === slice.label,
    );
    if (!selectedCategory) return;

    setDrillDown({
      open: true,
      title: `Service Category Mix: ${selectedCategory.category}`,
      subtitle: `${selectedCategory.clientCount} clients • ${selectedCategory.servicesCount} services`,
      description:
        "Clients contributing to this service category within the selected filters.",
      data: selectedCategory.clients || [],
      columns: [
        { field: "clientName", headerName: "Client", width: 280 },
        { field: "servicesCount", headerName: "Services", width: 110 },
        {
          field: "revenue",
          headerName: "Revenue",
          width: 140,
          renderCell: (row) => currencyCell(row.revenue),
        },
      ],
    });
  }, [currencyCell]);

  const handleServiceTypeBarClick = useCallback((item, data) => {
    const selectedType = (data.serviceTypeMix || []).find(
      (row) => row.serviceType === item.serviceType,
    );
    if (!selectedType) return;

    setDrillDown({
      open: true,
      title: `Service Type: ${selectedType.serviceType}`,
      subtitle: `${selectedType.servicesCount || 0} services • ${selectedType.category || "N/A"}`,
      description:
        "Performance details for the selected service type in the filtered period.",
      data: [selectedType],
      columns: [
        { field: "serviceType", headerName: "Service Type", width: 240 },
        { field: "category", headerName: "Category", width: 130 },
        { field: "servicesCount", headerName: "Services", width: 100 },
        {
          field: "revenue",
          headerName: "Revenue",
          width: 140,
          renderCell: (row) => currencyCell(row.revenue),
        },
        {
          field: "profitMargin",
          headerName: "Margin",
          width: 100,
          renderCell: (row) => `${(row.profitMargin || 0).toFixed(2)}%`,
        },
      ],
    });
  }, [currencyCell]);

  const metricsConfig = useCallback(
    (data) => [
      {
        groupTitle: "Client Activity",
        metrics: [
          {
            title: "Active Clients",
            value: data.activeClients || 0,
            subtitle: "With activity in period",
          },
          {
            title: "Total Services",
            value: data.totalServices || 0,
            subtitle: "Delivered in period",
          },
          {
            title: "Avg Services / Client",
            value: `${(data.avgServicesPerClient || 0).toFixed(2)}`,
            subtitle: "Activity intensity",
          },
          {
            title: "At-Risk Clients",
            value: data.atRiskClientsCount || 0,
            subtitle: "No service > 60 days",
            color:
              (data.atRiskClientsCount || 0) > 0
                ? "warning.main"
                : "success.main",
            onClick: () => handleAtRiskClientsClick(data),
          },
        ],
      },
    ],
    [handleAtRiskClientsClick],
  );

  const chartsConfig = useCallback(
    (data) => (
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <ReportPieChart
            title="Client Activity by Type"
            tooltipTitle="Client Type Activity Mix"
            tooltipContent="Shows activity distribution by client type. Click a slice to view the client list and details."
            data={(data.clientTypeBreakdown || []).map((item, idx) => ({
              id: idx + 1,
              label: item.type,
              value: item.count,
            }))}
            onSliceClick={(slice) => handleClientTypeSliceClick(slice, data)}
            height={340}
            showLegend={true}
            valueFormatter={(value) => `${value || 0} clients`}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <ReportPieChart
            title="Client Usage by Service Category"
            tooltipTitle="Category Mix by Client Count"
            tooltipContent="Shows unique client usage by service category. Click a slice to view contributing clients."
            data={(data.categoryMixBreakdown || []).map((item, idx) => ({
              id: idx + 1,
              label: item.category,
              value: item.clientCount,
            }))}
            onSliceClick={(slice) => handleCategorySliceClick(slice, data)}
            height={340}
            showLegend={true}
            valueFormatter={(value) => `${value || 0} clients`}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <ReportPieChart
            title="Top Service Types by Volume"
            tooltipTitle="Service Type Distribution"
            tooltipContent="Top service types ranked by service count in the selected period. Click a slice for details."
            data={(data.serviceTypeMix || []).slice(0, 10).map((item, idx) => ({
              id: idx + 1,
              label: item.serviceType,
              value: item.servicesCount,
            }))}
            onSliceClick={(item) =>
              handleServiceTypeBarClick({ serviceType: item.label }, data)
            }
            height={340}
            showLegend={true}
            valueFormatter={(value) => `${value || 0} services`}
          />
        </Grid>
      </Grid>
    ),
    [
      handleCategorySliceClick,
      handleClientTypeSliceClick,
      handleServiceTypeBarClick,
    ],
  );

  const dataFetchFn = useCallback(async (activeFilters) => {
    const [activityResponse, categoryResponse, serviceTypeResponse] =
      await Promise.all([
        api.get("/reports/client/activity", { params: activeFilters }),
        api.get("/reports/financial/services-by-category", {
          params: activeFilters,
        }),
        api.get("/reports/financial/revenue-by-service", {
          params: activeFilters,
        }),
      ]);

    const activityRows = activityResponse.data?.data?.tableData || [];
    const categoryServices = categoryResponse.data?.data?.services || [];
    const serviceTypeMix = serviceTypeResponse.data?.data?.tableData || [];

    const totalServices = activityRows.reduce(
      (sum, row) => sum + (row.servicesCount || 0),
      0,
    );
    const atRiskClients = activityRows
      .filter(
        (row) =>
          row.daysSinceLastService != null && Number(row.daysSinceLastService) > 60,
      )
      .sort((first, second) => second.daysSinceLastService - first.daysSinceLastService);

    const clientTypeMap = {};
    activityRows.forEach((row) => {
      const type = row.clientType || "Unknown";
      if (!clientTypeMap[type]) {
        clientTypeMap[type] = {
          type,
          count: 0,
          servicesCount: 0,
          revenue: 0,
          clients: [],
        };
      }

      clientTypeMap[type].count += 1;
      clientTypeMap[type].servicesCount += row.servicesCount || 0;
      clientTypeMap[type].revenue += row.revenue || 0;
      clientTypeMap[type].clients.push(row);
    });

    const clientTypeBreakdown = Object.values(clientTypeMap).sort(
      (first, second) => second.count - first.count,
    );

    const categoryMixMap = {};
    categoryServices.forEach((row) => {
      const category = row.category || "Uncategorized";
      const clientName = row.clientName || "Unknown Client";

      if (!categoryMixMap[category]) {
        categoryMixMap[category] = {
          category,
          servicesCount: 0,
          revenue: 0,
          clientsMap: {},
        };
      }

      categoryMixMap[category].servicesCount += 1;
      categoryMixMap[category].revenue += Number(row.AccountCharge || 0);

      if (!categoryMixMap[category].clientsMap[clientName]) {
        categoryMixMap[category].clientsMap[clientName] = {
          clientName,
          servicesCount: 0,
          revenue: 0,
        };
      }

      categoryMixMap[category].clientsMap[clientName].servicesCount += 1;
      categoryMixMap[category].clientsMap[clientName].revenue += Number(
        row.AccountCharge || 0,
      );
    });

    const categoryMixBreakdown = Object.values(categoryMixMap)
      .map((row) => ({
        category: row.category,
        servicesCount: row.servicesCount,
        revenue: row.revenue,
        clientCount: Object.keys(row.clientsMap).length,
        clients: Object.values(row.clientsMap).sort(
          (first, second) => second.revenue - first.revenue,
        ),
      }))
      .sort((first, second) => second.clientCount - first.clientCount);

    return {
      success: true,
      data: {
        activeClients: activityRows.length,
        totalServices,
        avgServicesPerClient:
          activityRows.length > 0 ? totalServices / activityRows.length : 0,
        atRiskClientsCount: atRiskClients.length,
        atRiskClients,
        clientTypeBreakdown,
        categoryMixBreakdown,
        serviceTypeMix,
        serviceTypeCount: serviceTypeMix.length,
        topServiceType: serviceTypeMix[0]?.serviceType || "N/A",
      },
    };
  }, []);

  return (
    <>
      <DashboardReport
        title="Client Activity & Service Mix"
        description="Client activity, service frequency, recency, and service mix patterns"
        dataFetchFn={dataFetchFn}
        filters={filters}
        metricsConfig={metricsConfig}
        chartsConfig={chartsConfig}
      />
      <DrillDownDrawer
        open={drillDown.open}
        onClose={closeDrillDown}
        title={drillDown.title}
        subtitle={drillDown.subtitle}
        description={drillDown.description}
        data={drillDown.data}
        columns={drillDown.columns}
        summary={drillDown.summary}
      />
    </>
  );
};

/**
 * Client Revenue & Profitability - Revenue concentration and client margins
 */
export const ClientRevenueProfitability = ({ filters }) => {
  const [drillDown, setDrillDown] = useState({
    open: false,
    title: "",
    subtitle: "",
    description: "",
    data: [],
    columns: [],
    summary: null,
  });

  const closeDrillDown = useCallback(() => {
    setDrillDown({
      open: false,
      title: "",
      subtitle: "",
      description: "",
      data: [],
      columns: [],
      summary: null,
    });
  }, []);

  const money = useCallback(
    (value) =>
    `$${(value || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`,
    [],
  );

  const clientColumns = useMemo(
    () => [
      { field: "clientName", headerName: "Client", width: 240 },
      { field: "clientType", headerName: "Type", width: 140 },
      {
        field: "revenue",
        headerName: "Revenue",
        width: 150,
        renderCell: (row) => money(row.revenue),
      },
      {
        field: "profit",
        headerName: "Profit",
        width: 150,
        renderCell: (row) => money(row.profit),
      },
      {
        field: "profitMargin",
        headerName: "Margin",
        width: 120,
        renderCell: (row) => `${(row.profitMargin || 0).toFixed(2)}%`,
      },
      { field: "servicesCount", headerName: "Services", width: 100 },
      {
        field: "avgRevenuePerService",
        headerName: "Avg / Service",
        width: 140,
        renderCell: (row) => money(row.avgRevenuePerService),
      },
    ],
    [money],
  );

  const openTopClients = useCallback((data) => {
    const topClients = data.tableData?.slice(0, 20) || [];
    setDrillDown({
      open: true,
      title: "Top Clients by Revenue",
      subtitle: `${topClients.length} clients`,
      description:
        "Highest revenue clients in the selected period with profitability context.",
      data: topClients,
      columns: clientColumns,
    });
  }, [clientColumns]);

  const openTop20Revenue = useCallback((data) => {
    const top20Count = Math.max(1, data.top20Count || 0);
    const top20Clients = data.tableData?.slice(0, top20Count) || [];
    setDrillDown({
      open: true,
      title: "Top 20% Revenue Contributors",
      subtitle: `${top20Clients.length} clients • ${money(data.top20Revenue)}`,
      description:
        "Clients that make up the top 20% by count and their combined revenue impact.",
      data: top20Clients,
      columns: clientColumns,
    });
  }, [clientColumns, money]);

  const openNewClients = useCallback((data) => {
    const newClients = (data.tableData || []).filter((row) => row.isNewClient);

    setDrillDown({
      open: true,
      title: "New Clients",
      subtitle: `${newClients.length} clients • ${money(data.newClientRevenue)}`,
      description:
        "Clients with revenue in the selected period and no revenue in the previous comparable period.",
      data: newClients,
      columns: clientColumns,
      summary: {
        metrics: [
          { label: "New Clients", value: newClients.length },
          { label: "Revenue", value: money(data.newClientRevenue) },
        ],
      },
    });
  }, [clientColumns, money]);

  const openRetainedClients = useCallback((data) => {
    const retainedClients = (data.tableData || []).filter(
      (row) => !row.isNewClient && (row.lastYearRevenue || 0) > 0,
    );

    setDrillDown({
      open: true,
      title: "Retained Clients",
      subtitle: `${retainedClients.length} clients • ${money(data.retainedClientRevenue)}`,
      description:
        "Clients that generated revenue in both the selected period and the previous comparable period.",
      data: retainedClients,
      columns: clientColumns,
      summary: {
        metrics: [
          { label: "Retained Clients", value: retainedClients.length },
          { label: "Revenue", value: money(data.retainedClientRevenue) },
        ],
      },
    });
  }, [clientColumns, money]);

  const openClientType = useCallback((slice, data) => {
    const selectedType = data.clientTypeBreakdown?.find(
      (row) => row.type === slice.label,
    );
    if (!selectedType) return;

    const typedRows = (data.tableData || []).filter(
      (row) => (row.clientType || "Unknown") === selectedType.type,
    );

    setDrillDown({
      open: true,
      title: `Client Type: ${selectedType.type}`,
      subtitle: `${selectedType.count} clients • ${money(selectedType.revenue)}`,
      description: "Revenue and profitability by selected client type.",
      data: typedRows,
      columns: clientColumns,
      summary: {
        metrics: [
          { label: "Type", value: selectedType.type },
          { label: "Clients", value: selectedType.count },
          { label: "Revenue", value: money(selectedType.revenue) },
          { label: "Share", value: `${(selectedType.percentage || 0).toFixed(2)}%` },
        ],
      },
    });
  }, [clientColumns, money]);

  const metricsConfig = useCallback(
    (data) => [
      {
        groupTitle: "Revenue & Margin",
        metrics: [
          {
            title: "Total Revenue",
            value: money(data.totalRevenue),
            subtitle: "Selected period",
          },
          {
            title: "Total Profit",
            value: money(data.totalProfit),
            subtitle: "Revenue minus direct cost",
          },
          {
            title: "Avg Profit Margin",
            value: `${(data.avgProfitMargin || 0).toFixed(2)}%`,
            subtitle: "Across active clients",
          },
          {
            title: "Top 20% Concentration",
            value: `${(data.concentrationRatio || 0).toFixed(2)}%`,
            subtitle: "Revenue from top 20% clients",
            color:
              (data.concentrationRatio || 0) >= 80
                ? "warning.main"
                : "success.main",
            onClick: () => openTop20Revenue(data),
          },
        ],
      },
      {
        groupTitle: "Client Dynamics",
        metrics: [
          {
            title: "Total Active Clients",
            value: data.totalClients || 0,
            subtitle: "Clients with revenue",
            onClick: () => openTopClients(data),
          },
          {
            title: "New Clients",
            value: data.newClientsCount || 0,
            subtitle: money(data.newClientRevenue),
            onClick: () => openNewClients(data),
          },
          {
            title: "Retained Clients",
            value: data.retainedClientsCount || 0,
            subtitle: money(data.retainedClientRevenue),
            onClick: () => openRetainedClients(data),
          },
          {
            title: "YoY Growth",
            value: `${(data.yoyGrowth || 0).toFixed(2)}%`,
            subtitle: `Last year: ${money(data.lastYearRevenue)}`,
            color:
              (data.yoyGrowth || 0) >= 0 ? "success.main" : "error.main",
          },
        ],
      },
    ],
    [openNewClients, openRetainedClients, openTop20Revenue, openTopClients, money],
  );

  const chartsConfig = useCallback(
    (data) => (
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <ReportPieChart
            title="Top Clients Revenue Share"
            tooltipTitle="Top Client Concentration"
            tooltipContent="Shows how revenue is distributed across top revenue clients. Click a slice for client-type drill-downs in the other chart."
            data={(data.chartData || []).slice(0, 10).map((item) => ({
              id: item.id,
              label: item.label,
              value: item.value,
            }))}
            height={350}
            showLegend={true}
            valueFormatter={(value) => money(value)}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <ReportPieChart
            title="Revenue by Client Type"
            tooltipTitle="Client Type Revenue Mix"
            tooltipContent="Revenue share by client type. Click a slice to view detailed client list for that type."
            data={(data.clientTypeBreakdown || []).map((item, idx) => ({
              id: idx + 1,
              label: item.type,
              value: item.revenue,
            }))}
            onSliceClick={(slice) => openClientType(slice, data)}
            height={350}
            showLegend={true}
            valueFormatter={(value) => money(value)}
          />
        </Grid>
      </Grid>
    ),
    [openClientType, money],
  );

  const dataFetchFn = useCallback(async (activeFilters) => {
    const response = await api.get("/reports/financial/revenue-by-client", {
      params: activeFilters,
    });
    return response.data;
  }, []);

  return (
    <>
      <DashboardReport
        title="Client Revenue & Profitability"
        description="Client-level revenue, profit contribution, concentration, and growth metrics"
        dataFetchFn={dataFetchFn}
        filters={filters}
        metricsConfig={metricsConfig}
        chartsConfig={chartsConfig}
      />
      <DrillDownDrawer
        open={drillDown.open}
        onClose={closeDrillDown}
        title={drillDown.title}
        subtitle={drillDown.subtitle}
        description={drillDown.description}
        data={drillDown.data}
        columns={drillDown.columns}
        summary={drillDown.summary}
      />
    </>
  );
};

/**
 * Client Retention & Risk - Retention health and risk signal dashboard
 */
export const ClientRetentionRisk = ({ filters }) => {
  const [drillDown, setDrillDown] = useState({
    open: false,
    title: "",
    subtitle: "",
    description: "",
    data: [],
    columns: [],
    summary: null,
  });

  const closeDrillDown = useCallback(() => {
    setDrillDown({
      open: false,
      title: "",
      subtitle: "",
      description: "",
      data: [],
      columns: [],
      summary: null,
    });
  }, []);

  const money = useCallback(
    (value) =>
    `$${(value || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`,
    [],
  );

  const retentionColumns = useMemo(
    () => [
      { field: "name", headerName: "Client", width: 260 },
      { field: "type", headerName: "Type", width: 140 },
      {
        field: "revenue",
        headerName: "Revenue",
        width: 140,
        renderCell: (row) => money(row.revenue),
      },
      {
        field: "previousRevenue",
        headerName: "Previous Revenue",
        width: 150,
        renderCell: (row) =>
          row.previousRevenue != null ? money(row.previousRevenue) : "-",
      },
      {
        field: "revenueChange",
        headerName: "Change",
        width: 140,
        renderCell: (row) => {
          if (row.revenueChange == null) return "-";
          const sign = row.revenueChange > 0 ? "+" : "";
          return `${sign}${money(row.revenueChange)}`;
        },
      },
      { field: "servicesCount", headerName: "Services", width: 100 },
      {
        field: "lastServiceDate",
        headerName: "Last Service",
        width: 130,
        renderCell: (row) =>
          row.lastServiceDate
            ? new Date(row.lastServiceDate).toLocaleDateString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
              })
            : "-",
      },
    ],
    [money],
  );

  const activityColumns = useMemo(
    () => [
      { field: "clientName", headerName: "Client", width: 260 },
      { field: "clientType", headerName: "Type", width: 140 },
      { field: "servicesCount", headerName: "Services", width: 100 },
      {
        field: "revenue",
        headerName: "Revenue",
        width: 140,
        renderCell: (row) => money(row.revenue),
      },
      {
        field: "daysSinceLastService",
        headerName: "Days Since Last Service",
        width: 170,
        renderCell: (row) => row.daysSinceLastService ?? "-",
      },
      {
        field: "lastServiceDate",
        headerName: "Last Service",
        width: 130,
        renderCell: (row) =>
          row.lastServiceDate
            ? new Date(row.lastServiceDate).toLocaleDateString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
              })
            : "-",
      },
    ],
    [money],
  );

  const paymentColumns = useMemo(
    () => [
      { field: "clientName", headerName: "Client", width: 260 },
      {
        field: "avgPaymentDays",
        headerName: "Avg Payment Days",
        width: 150,
        renderCell: (row) =>
          row.avgPaymentDays != null ? `${row.avgPaymentDays} days` : "-",
      },
      { field: "invoiceCount", headerName: "Invoices", width: 100 },
      {
        field: "totalRevenue",
        headerName: "Revenue",
        width: 140,
        renderCell: (row) => money(row.totalRevenue),
      },
      {
        field: "daysPastDue",
        headerName: "Max Days Past Due",
        width: 160,
        renderCell: (row) =>
          row.daysPastDue != null ? `${row.daysPastDue} days` : "Not overdue",
      },
    ],
    [money],
  );

  const openRetentionBucket = useCallback((bucket, data) => {
    const buckets = {
      Retained: data.retentionDetails?.retainedClients || [],
      Churned: data.retentionDetails?.churnedClients || [],
      New: data.retentionDetails?.newClients || [],
    };

    const rows = buckets[bucket] || [];
    setDrillDown({
      open: true,
      title: `${bucket} Clients`,
      subtitle: `${rows.length} clients`,
      description:
        bucket === "Retained"
          ? "Clients active in both the selected and previous comparable period."
          : bucket === "Churned"
            ? "Clients active in the previous comparable period but not in the selected period."
            : "Clients with activity in the selected period and no activity in the previous comparable period.",
      data: rows,
      columns: retentionColumns,
    });
  }, [retentionColumns]);

  const openAtRiskActivity = useCallback((data) => {
    setDrillDown({
      open: true,
      title: "Activity Risk: No Service > 60 Days",
      subtitle: `${data.activityRiskCount || 0} clients`,
      description:
        "Clients with over 60 days since last service, indicating potential engagement decline.",
      data: data.activityRiskClients || [],
      columns: activityColumns,
    });
  }, [activityColumns]);

  const openSlowPayers = useCallback((data) => {
    setDrillDown({
      open: true,
      title: "Payment Risk: Slow Payers (60+ Days)",
      subtitle: `${data.slowPayerCount || 0} clients`,
      description:
        "Clients flagged by either 60+ average payment days on paid invoices or 60+ days past due on unpaid invoices.",
      data: data.slowPayerClients || [],
      columns: paymentColumns,
    });
  }, [paymentColumns]);

  const openDecliningRetained = useCallback((data) => {
    setDrillDown({
      open: true,
      title: "Retained Clients with Revenue Decline",
      subtitle: `${data.decliningRetainedCount || 0} clients`,
      description:
        "Retained clients whose current-period revenue is below the previous comparable period.",
      data: data.decliningRetainedClients || [],
      columns: retentionColumns,
    });
  }, [retentionColumns]);

  const openPaymentRange = useCallback((slice, data) => {
    const selected = (data.paymentRanges || []).find(
      (row) => row.label === slice.label,
    );
    if (!selected) return;

    setDrillDown({
      open: true,
      title: `Payment Range: ${selected.label}`,
      subtitle: `${selected.clientCount || 0} clients • ${money(selected.totalRevenue)}`,
      description:
        "Clients grouped by average payment days calculated from invoice due date to payment received date.",
      data: selected.clients || [],
      columns: paymentColumns,
    });
  }, [paymentColumns, money]);

  const openRiskSignal = useCallback((slice, data) => {
    if (slice.label === "Slow Payers (60+)") {
      openSlowPayers(data);
      return;
    }
    if (slice.label === "Declining Retained") {
      openDecliningRetained(data);
      return;
    }
    if (slice.label === "Low Activity (60+)") {
      openAtRiskActivity(data);
    }
  }, [openAtRiskActivity, openDecliningRetained, openSlowPayers]);

  const metricsConfig = useCallback(
    (data) => [
      {
        groupTitle: "Retention",
        metrics: [
          {
            title: "Retention Rate",
            value: `${(data.retentionSummary?.retentionRate || 0).toFixed(2)}%`,
            subtitle: `${data.retentionSummary?.retained || 0} retained`,
            color:
              (data.retentionSummary?.retentionRate || 0) >= 90
                ? "success.main"
                : "warning.main",
            onClick: () => openRetentionBucket("Retained", data),
          },
          {
            title: "Churned Clients",
            value: data.retentionSummary?.churned || 0,
            subtitle: `${(data.retentionSummary?.churnRate || 0).toFixed(2)}% churn`,
            color:
              (data.retentionSummary?.churned || 0) > 0
                ? "error.main"
                : "success.main",
            onClick: () => openRetentionBucket("Churned", data),
          },
          {
            title: "New Clients",
            value: data.retentionSummary?.new || 0,
            subtitle: "Current-period additions",
            onClick: () => openRetentionBucket("New", data),
          },
        ],
      },
      {
        groupTitle: "Risk Signals",
        metrics: [
          {
            title: "Slow Payers (60+)",
            value: data.slowPayerCount || 0,
            subtitle: "Payment behavior risk",
            color:
              (data.slowPayerCount || 0) > 0 ? "warning.main" : "success.main",
            onClick: () => openSlowPayers(data),
          },
          {
            title: "Low Activity (60+)",
            value: data.activityRiskCount || 0,
            subtitle: "No service in 60+ days",
            color:
              (data.activityRiskCount || 0) > 0
                ? "warning.main"
                : "success.main",
            onClick: () => openAtRiskActivity(data),
          },
          {
            title: "Declining Retained",
            value: data.decliningRetainedCount || 0,
            subtitle: "Revenue down vs prior period",
            color:
              (data.decliningRetainedCount || 0) > 0
                ? "warning.main"
                : "success.main",
            onClick: () => openDecliningRetained(data),
          },
        ],
      },
    ],
    [
      openAtRiskActivity,
      openDecliningRetained,
      openRetentionBucket,
      openSlowPayers,
    ],
  );

  const chartsConfig = useCallback(
    (data) => (
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <ReportPieChart
            title="Retention Status"
            tooltipTitle="Retained vs Churned vs New"
            tooltipContent="Compares retained, churned, and new client counts across the selected period and its previous comparable period."
            data={[
              {
                id: 1,
                label: "Retained",
                value: data.retentionSummary?.retained || 0,
              },
              {
                id: 2,
                label: "Churned",
                value: data.retentionSummary?.churned || 0,
              },
              {
                id: 3,
                label: "New",
                value: data.retentionSummary?.new || 0,
              },
            ]}
            onSliceClick={(slice) => openRetentionBucket(slice.label, data)}
            height={340}
            showLegend={true}
            valueFormatter={(value) => `${value || 0} clients`}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <ReportPieChart
            title="Payment Risk Ranges"
            tooltipTitle="Average Payment Speed"
            tooltipContent="Distribution of clients by average days to payment. Click a slice to view clients in that range."
            data={(data.paymentRanges || []).map((row, idx) => ({
              id: idx + 1,
              label: row.label,
              value: row.clientCount || 0,
            }))}
            onSliceClick={(slice) => openPaymentRange(slice, data)}
            height={340}
            showLegend={true}
            valueFormatter={(value) => `${value || 0} clients`}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <ReportPieChart
            title="Risk Signals"
            tooltipTitle="Core Risk Indicators"
            tooltipContent="Snapshot of key client risk indicators: slow payments, low activity recency, and declining retained revenue."
            data={[
              {
                id: 1,
                label: "Slow Payers (60+)",
                value: data.slowPayerCount || 0,
              },
              {
                id: 2,
                label: "Low Activity (60+)",
                value: data.activityRiskCount || 0,
              },
              {
                id: 3,
                label: "Declining Retained",
                value: data.decliningRetainedCount || 0,
              },
            ]}
            onSliceClick={(slice) => openRiskSignal(slice, data)}
            height={340}
            showLegend={true}
            valueFormatter={(value) => `${value || 0} clients`}
          />
        </Grid>
      </Grid>
    ),
    [openPaymentRange, openRetentionBucket, openRiskSignal],
  );

  const dataFetchFn = useCallback(async (activeFilters) => {
    const [retentionResponse, paymentResponse, activityResponse, outstandingResponse] =
      await Promise.all([
        api.get("/reports/financial/client-retention-details", {
          params: activeFilters,
        }),
        api.get("/reports/financial/payment-ranges", {
          params: activeFilters,
        }),
        api.get("/reports/client/activity", {
          params: activeFilters,
        }),
        api.get("/reports/invoice/outstanding", {
          params: activeFilters,
        }),
      ]);

    const retentionDetails = retentionResponse.data?.data || {
      retainedClients: [],
      churnedClients: [],
      newClients: [],
      summary: { retained: 0, churned: 0, new: 0, retentionRate: 0, churnRate: 0 },
    };
    const paymentRanges = paymentResponse.data?.data?.ranges || [];
    const activityRows = activityResponse.data?.data?.tableData || [];
    const outstandingInvoices = outstandingResponse.data?.data?.invoices || [];

    const allPaymentClients = paymentRanges.flatMap((range) => range.clients || []);

    const overdueByClientMap = {};
    outstandingInvoices
      .filter((invoice) => Number(invoice.daysPastDue || 0) > 60)
      .forEach((invoice) => {
        const key = `${invoice.clientId || "na"}|||${invoice.clientName || "Unknown"}`;
        if (!overdueByClientMap[key]) {
          overdueByClientMap[key] = {
            daysPastDue: 0,
          };
        }

        overdueByClientMap[key].daysPastDue = Math.max(
          overdueByClientMap[key].daysPastDue,
          Number(invoice.daysPastDue || 0),
        );
      });

    const paymentClientMap = {};
    allPaymentClients.forEach((client) => {
      const key = `${client.clientId || "na"}|||${client.clientName || "Unknown"}`;
      paymentClientMap[key] = {
        ...client,
        daysPastDue: overdueByClientMap[key]?.daysPastDue ?? null,
      };
    });

    Object.entries(overdueByClientMap).forEach(([key, overdue]) => {
      if (paymentClientMap[key]) {
        return;
      }

      const [clientIdRaw, clientName] = key.split("|||");
      paymentClientMap[key] = {
        clientId: clientIdRaw === "na" ? null : Number(clientIdRaw),
        clientName: clientName || "Unknown",
        avgPaymentDays: overdue.daysPastDue,
        invoiceCount: 0,
        totalRevenue: 0,
        daysPastDue: overdue.daysPastDue,
      };
    });

    const slowPayerClients = Object.values(paymentClientMap)
      .filter(
        (client) =>
          Number(client.avgPaymentDays || 0) >= 60 ||
          Number(client.daysPastDue || 0) > 60,
      )
      .map((client) => {
        return {
          ...client,
          avgPaymentDays:
            client.avgPaymentDays != null
              ? Number(client.avgPaymentDays)
              : Number(client.daysPastDue || 0),
        };
      })
      .sort((a, b) => Number(b.avgPaymentDays || 0) - Number(a.avgPaymentDays || 0));

    const activityRiskClients = activityRows
      .filter(
        (row) =>
          row.daysSinceLastService != null && Number(row.daysSinceLastService) >= 60,
      )
      .sort((a, b) => (b.daysSinceLastService || 0) - (a.daysSinceLastService || 0));

    const decliningRetainedClients = (retentionDetails.retainedClients || [])
      .filter((row) => Number(row.revenueChange || 0) < 0)
      .sort((a, b) => Number(a.revenueChange || 0) - Number(b.revenueChange || 0));

    return {
      success: true,
      data: {
        retentionDetails,
        retentionSummary: retentionDetails.summary || {
          retained: 0,
          churned: 0,
          new: 0,
          retentionRate: 0,
          churnRate: 0,
        },
        paymentRanges,
        slowPayerClients,
        slowPayerCount: slowPayerClients.length,
        activityRiskClients,
        activityRiskCount: activityRiskClients.length,
        decliningRetainedClients,
        decliningRetainedCount: decliningRetainedClients.length,
      },
    };
  }, []);

  return (
    <>
      <DashboardReport
        title="Client Retention & Risk"
        description="Retention/churn trends with risk signals from payment behavior, activity recency, and revenue decline"
        dataFetchFn={dataFetchFn}
        filters={filters}
        metricsConfig={metricsConfig}
        chartsConfig={chartsConfig}
      />
      <DrillDownDrawer
        open={drillDown.open}
        onClose={closeDrillDown}
        title={drillDown.title}
        subtitle={drillDown.subtitle}
        description={drillDown.description}
        data={drillDown.data}
        columns={drillDown.columns}
        summary={drillDown.summary}
      />
    </>
  );
};

/**
 * Client Quality & SLA Report - service reliability and SLA adherence by client
 */
export const ClientQualitySLA = ({ filters }) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);

  const [drillDown, setDrillDown] = useState({
    open: false,
    title: "",
    subtitle: "",
    description: "",
    data: [],
    columns: [],
    summary: null,
  });

  const closeDrillDown = useCallback(() => {
    setDrillDown({
      open: false,
      title: "",
      subtitle: "",
      description: "",
      data: [],
      columns: [],
      summary: null,
    });
  }, []);

  const formatCurrency = useCallback(
    (value) =>
      `$${(value || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
    [],
  );

  const dataFetchFn = useCallback(async (activeFilters) => {
    const response = await api.get("/reports/client/sla", {
      params: activeFilters,
    });
    return response.data;
  }, []);

  const openDrawer = useCallback((title, subtitle, description, rows) => {
    setDrillDown({
      open: true,
      title,
      subtitle,
      description,
      data: rows,
      columns: [
        { field: "clientName", headerName: "Client", width: 250 },
        { field: "servicesCount", headerName: "Services", width: 100 },
        {
          field: "revenue",
          headerName: "Revenue",
          width: 130,
          renderCell: (row) => formatCurrency(row.revenue),
        },
        {
          field: "completionRate",
          headerName: "Completion",
          width: 110,
          renderCell: (row) => `${(row.completionRate || 0).toFixed(1)}%`,
        },
        {
          field: "onTimeRate",
          headerName: "On-Time",
          width: 100,
          renderCell: (row) => `${(row.onTimeRate || 0).toFixed(1)}%`,
        },
        {
          field: "cancellationRate",
          headerName: "Cancellation",
          width: 120,
          renderCell: (row) => `${(row.cancellationRate || 0).toFixed(1)}%`,
        },
        {
          field: "avgLateDays",
          headerName: "Avg Late Days",
          width: 120,
          renderCell: (row) => `${(row.avgLateDays || 0).toFixed(1)}d`,
        },
        { field: "riskLevel", headerName: "Risk", width: 100 },
      ],
    });
  }, [formatCurrency]);

  const metricsConfig = useCallback(
    (data) => {
      const summary = data.summary || {};
      const clientRows = data.clientRows || [];

      const highRiskClients = clientRows.filter(
        (row) => row.riskLevel === "High",
      );

      return [
        {
          groupTitle: "SLA Health",
          metrics: [
            {
              title: "On-Time SLA Rate",
              value: `${(summary.onTimeRate || 0).toFixed(2)}%`,
              subtitle: `${summary.onTimeServices || 0}/${summary.slaTrackedServices || 0} SLA-tracked services`,
              tooltipTitle: "On-Time SLA Rate",
              tooltipContent: `Measures delivery punctuality for SLA-tracked work.

Formula:
On-Time SLA Rate = (On-Time Services / SLA-Tracked Services) x 100

Current period context:
- On-Time Services: ${summary.onTimeServices || 0}
- SLA-Tracked Services: ${summary.slaTrackedServices || 0}
- Current Rate: ${(summary.onTimeRate || 0).toFixed(2)}%

How to interpret:
- 95%+ = strong reliability for enterprise-grade SLA commitments
- 90-95% = acceptable but monitor for client-specific slippage
- <90% = material risk of dissatisfaction, escalations, and renewal pressure

Operational use:
- Pair this with cancellation trend and avg late days by client
- Click this card to identify accounts dragging SLA performance
- Prioritize intervention on high-revenue clients with repeated misses`,
              color: "success.main",
              onClick: () =>
                openDrawer(
                  "Client SLA Performance",
                  `${clientRows.length} clients`,
                  "Client-level SLA adherence and delivery reliability.",
                  clientRows,
                ),
            },
            {
              title: "Completion Rate",
              value: `${(summary.completionRate || 0).toFixed(2)}%`,
              subtitle: `${summary.completedServices || 0}/${summary.totalServices || 0} completed`,
              tooltipTitle: "Completion Rate",
              tooltipContent: `Tracks execution consistency across all scheduled services.

Formula:
Completion Rate = (Completed Services / Total Services) x 100

Current period context:
- Completed Services: ${summary.completedServices || 0}
- Total Services: ${summary.totalServices || 0}
- Current Rate: ${(summary.completionRate || 0).toFixed(2)}%

How to interpret:
- High completion with weak on-time rate = capacity strain (work gets done but late)
- Low completion and high cancellations = scheduling, staffing, or intake quality problem
- Sudden drops usually indicate process breakpoints in dispatch or linguist availability

Operational use:
- Use client drilldown to isolate where completion failures cluster
- Compare high-volume clients versus long-tail clients
- Review service mix to see whether one delivery mode is the failure driver`,
              onClick: () =>
                openDrawer(
                  "Client Completion Performance",
                  `${clientRows.length} clients`,
                  "Completion reliability by client.",
                  clientRows,
                ),
            },
            {
              title: "Cancellation Rate",
              value: `${(summary.cancellationRate || 0).toFixed(2)}%`,
              subtitle: `${summary.cancelledServices || 0} cancelled services`,
              tooltipTitle: "Cancellation Rate",
              tooltipContent: `Quantifies service churn before delivery completion.

Formula:
Cancellation Rate = (Cancelled Services / Total Services) x 100

Current period context:
- Cancelled Services: ${summary.cancelledServices || 0}
- Total Services: ${summary.totalServices || 0}
- Current Rate: ${(summary.cancellationRate || 0).toFixed(2)}%

Common drivers:
- Late assignment of linguists
- Client schedule volatility or poor lead time
- Mismatch between required language/specialty and available talent
- Process friction in confirmation workflows

Operational use:
- Sort client drilldown by highest cancellation rate first
- Cross-check cancellation spikes against SLA declines by month
- Triage high-cancel/high-revenue accounts for immediate account management action`,
              color: "warning.main",
              onClick: () =>
                openDrawer(
                  "Client Cancellation Performance",
                  `${clientRows.length} clients`,
                  "Cancellation pressure by client.",
                  clientRows.sort(
                    (a, b) => (b.cancellationRate || 0) - (a.cancellationRate || 0),
                  ),
                ),
            },
            {
              title: "High Risk Clients",
              value: highRiskClients.length,
              subtitle: formatCurrency(
                highRiskClients.reduce((sum, row) => sum + (row.revenue || 0), 0),
              ),
              tooltipTitle: "High Risk Clients",
              tooltipContent: `Counts accounts currently flagged as elevated SLA/quality risk.

Risk classification uses combined signals such as:
- Weaker on-time performance
- Lower completion reliability
- Higher cancellation pressure
- Delivery delay behavior (avg late days)

Current period context:
- High-Risk Clients: ${highRiskClients.length}
- Revenue at Risk: ${formatCurrency(
                highRiskClients.reduce((sum, row) => sum + (row.revenue || 0), 0),
              )}

How to use this metric:
- Treat as an intervention queue, not just a count
- Prioritize by revenue impact and severity of SLA degradation
- Build account recovery plans with operations + client success
- Track whether high-risk count declines after corrective actions`,
              color: "error.main",
              onClick: () =>
                openDrawer(
                  "High Risk Client Accounts",
                  `${highRiskClients.length} clients`,
                  "Accounts with weak SLA performance and/or elevated cancellation risk.",
                  highRiskClients,
                ),
            },
          ],
        },
      ];
    },
    [formatCurrency, openDrawer],
  );

  const chartsConfig = useCallback(
    (data) => {
      const monthly = data.monthlyTrend || {};
      const monthlyRows = (monthly.months || []).map((month, idx) => ({
        month,
        onTimeRate: monthly.onTimeRate?.[idx] || 0,
        completionRate: monthly.completionRate?.[idx] || 0,
        cancellationRate: monthly.cancellationRate?.[idx] || 0,
        servicesCount: monthly.servicesCount?.[idx] || 0,
        revenue: monthly.revenue?.[idx] || 0,
      }));

      return (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <ReportLineChart
              title="Monthly SLA vs Completion Trend"
              tooltipTitle="Monthly SLA vs Completion Trend"
              tooltipContent={`Compares three quality streams over time:
- On-Time SLA % (punctuality)
- Completion % (execution reliability)
- Cancellation % (service loss)

How to read patterns:
- On-Time down, Completion stable: delivery latency issue
- Completion down, Cancellation up: fulfillment or intake breakdown
- All three deteriorate together: broad operational stress

Management use:
- Validate whether quality programs are improving outcomes month to month
- Identify early warning periods before client churn or escalation
- Use with client-level drilldown to isolate which accounts drove trend changes`}
              xAxisData={monthlyRows.map((row) => row.month)}
              series={[
                {
                  data: monthlyRows.map((row) => row.onTimeRate),
                  label: "On-Time SLA %",
                  color: chartColors.success,
                },
                {
                  data: monthlyRows.map((row) => row.completionRate),
                  label: "Completion %",
                  color: chartColors.info,
                },
                {
                  data: monthlyRows.map((row) => row.cancellationRate),
                  label: "Cancellation %",
                  color: chartColors.warning,
                },
              ]}
              height={360}
              chartMargin={{ top: 64, right: 120, bottom: 50, left: 80 }}
              yAxisLabel="Rate (%)"
              valueFormatter={(value) => `${(value || 0).toFixed(1)}%`}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <ReportPieChart
              title="Client SLA Risk Distribution"
              tooltipTitle="Client SLA Risk Distribution"
              tooltipContent={`Shows portfolio concentration of client SLA risk tiers.

Why it matters:
- A growing High-Risk slice means rising service-quality exposure
- A large Medium-Risk segment is your pipeline for future deterioration
- Healthy portfolios skew Low-Risk with controlled Medium-Risk volume

Operational use:
- Allocate account management bandwidth by tier size
- Set quarterly targets to migrate clients from High -> Medium -> Low
- Monitor whether intervention programs reduce high-risk concentration`}
              data={data.riskDistribution || []}
              height={360}
              valueFormatter={(value) => `${value || 0} clients`}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <ReportBarChart
              title="Top Client SLA Risk"
              tooltipTitle="Top Client SLA Risk"
              tooltipContent={`Ranks highest-risk client accounts and compares:
- On-Time SLA % (service punctuality)
- Cancellation % (delivery instability)

How to interpret:
- Low on-time + high cancellation = urgent recovery account
- Improving on-time with flat cancellations = partial stabilization
- Strong variance across clients = localized account/process issues

Operational use:
- Click a bar to open client diagnostics and investigate root causes
- Use this view to sequence outreach, staffing fixes, and SLA remediation
- Re-check monthly to confirm risk mitigation is actually working`}
              data={(data.clientRows || []).slice(0, 12).map((row) => ({
                clientName: row.clientName,
                onTimeRate: row.onTimeRate || 0,
                cancellationRate: row.cancellationRate || 0,
              }))}
              xAxisKey="clientName"
              series={[
                {
                  dataKey: "onTimeRate",
                  label: "On-Time SLA %",
                  color: chartColors.success,
                },
                {
                  dataKey: "cancellationRate",
                  label: "Cancellation %",
                  color: chartColors.error,
                },
              ]}
              height={360}
              yAxisLabel="Rate (%)"
              yAxisScaleType="linear"
              valueFormatter={(value) => `${(value || 0).toFixed(1)}%`}
              onBarClick={(bar) =>
                openDrawer(
                  `Client SLA Detail: ${bar.clientName}`,
                  "Single client SLA profile",
                  "Detailed quality and SLA metrics for selected client.",
                  (data.clientRows || []).filter(
                    (row) => row.clientName === bar.clientName,
                  ),
                )
              }
            />
          </Grid>
        </Grid>
      );
    },
    [
      chartColors.error,
      chartColors.info,
      chartColors.success,
      chartColors.warning,
      openDrawer,
    ],
  );

  return (
    <>
      <DashboardReport
        title="Client Quality & SLA"
        description="SLA adherence, completion reliability, and cancellation risk by client account."
        dataFetchFn={dataFetchFn}
        filters={filters}
        metricsConfig={metricsConfig}
        chartsConfig={chartsConfig}
      />

      <DrillDownDrawer
        open={drillDown.open}
        onClose={closeDrillDown}
        title={drillDown.title}
        subtitle={drillDown.subtitle}
        description={drillDown.description}
        data={drillDown.data}
        columns={drillDown.columns}
        summary={drillDown.summary}
      />
    </>
  );
};

// Export all client report components
export const CLIENT_REPORTS = {
  "client-overview": ClientOverview,
  "client-list": ClientDirectoryContracts,
  "client-activity": ClientActivityServiceMix,
  "top-clients": ClientRevenueProfitability,
  "client-retention": ClientRetentionRisk,
  "sla-compliance": ClientQualitySLA,
  // Future client reports can be added here
};
